import { useCallback, useMemo, useState } from 'react';
import { useOrganizationAppointmentTypes } from '@features/appointment-types';
import { useClinicians } from '@features/organizations';
import { useOrganizationId, useSubjectLanguageVariants } from '@features/user';
import { useEventTracking } from '@services/statsig';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useSnackbar } from 'notistack';

import {
  useAcceptProspectMutation,
  useApproveChangeRequestMutation,
  useBookingRequestsQuery,
  useChangeRequestsQuery,
  useDeclineBookingRequestMutation,
  useDeclineChangeRequestMutation,
} from '../store/api';

import {
  BookingRequestsPanel,
  type RequestCardData,
  type ResolvedRow,
} from './BookingRequestsPanel';
import {
  IdentityDecisionDialog,
  type IdentityCandidate,
} from './IdentityDecisionDialog';

interface BookingRequestsPanelContainerProps {
  onClose: () => void;
  /** Jump the calendar to a request's day (ISO datetime). */
  onNavigateToDate?: (isoDate: string) => void;
}

interface PendingDecision {
  card: RequestCardData;
  candidates: IdentityCandidate[];
}

function decisionError(error: unknown): string {
  const maybeQueryError = error as FetchBaseQueryError | SerializedError;
  if (
    'data' in maybeQueryError &&
    maybeQueryError.data &&
    typeof maybeQueryError.data === 'object' &&
    (maybeQueryError.data as { error_code?: string }).error_code ===
      'slot_unavailable'
  ) {
    return 'That requested time was just taken — decline the request or ask the patient to pick again.';
  }
  return "We couldn't apply that decision. Please try again.";
}

/** Data + decisions for the booking-requests queue: fetches bookings and
 * change requests, enriches with provider/service names, and drives the
 * accept flow (identity resolved inside Confirm — GDN-2436). */
export function BookingRequestsPanelContainer({
  onClose,
  onNavigateToDate,
}: BookingRequestsPanelContainerProps) {
  const organizationId = useOrganizationId();
  const { plural, singular } = useSubjectLanguageVariants();
  const { enqueueSnackbar } = useSnackbar();
  const { bookingRequests, error: errorTracking } = useEventTracking();

  const { data, isLoading } = useBookingRequestsQuery(
    {},
    { pollingInterval: 60_000 },
  );
  const { data: changeRequestData } = useChangeRequestsQuery(undefined, {
    pollingInterval: 60_000,
  });
  const { clinicians } = useClinicians();
  const { appointmentTypes } = useOrganizationAppointmentTypes(organizationId);

  const [acceptProspect] = useAcceptProspectMutation();
  const [declineBooking] = useDeclineBookingRequestMutation();
  const [approveChangeRequest] = useApproveChangeRequestMutation();
  const [declineChangeRequest] = useDeclineChangeRequestMutation();

  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [decision, setDecision] = useState<PendingDecision | null>(null);
  const [resolved, setResolved] = useState<ResolvedRow[]>([]);

  const providerNames = useMemo(
    () => new Map(clinicians.map(c => [c.userId, c.name])),
    [clinicians],
  );
  const serviceNames = useMemo(
    () =>
      new Map(
        appointmentTypes.map(t => [t.encounter_type_id, t.encounter_type]),
      ),
    [appointmentTypes],
  );

  const requests: RequestCardData[] = useMemo(() => {
    const bookings: RequestCardData[] = (data?.items ?? []).map(item => {
      const gateResults = item.gate_results ?? [];
      const blockers: string[] = [];
      const paperwork = gateResults.find(result => result.gate === 'paperwork');
      if (paperwork && paperwork.outcome !== 'pass') {
        blockers.push('Paperwork pending');
      }
      const card = gateResults.find(result => result.gate === 'card');
      if (card && card.outcome !== 'pass' && !item.has_card_on_file) {
        blockers.push('Card required');
      }
      return {
        key: `booking-${item.booking_prospect_id}`,
        kind: 'booking' as const,
        bookingProspectId: item.booking_prospect_id,
        appointmentId: item.appointment_id,
        name:
          [item.first_name, item.last_name].filter(Boolean).join(' ') ||
          'Unknown requester',
        providerName: providerNames.get(item.provider_id) ?? null,
        startTime: item.appointment_start_time ?? null,
        durationMinutes: item.duration_minutes ?? null,
        serviceName: item.encounter_type_id
          ? (serviceNames.get(item.encounter_type_id) ?? null)
          : null,
        createdAt: item.created_at ?? null,
        email: item.email,
        phone: item.phone,
        reason: item.reason,
        blockers,
        autoConfirmed: item.appointment_status === 'scheduled',
        rescheduledByPatient: item.rescheduled_by_patient,
      };
    });
    const reschedules: RequestCardData[] = (changeRequestData?.items ?? []).map(
      request => ({
        key: `reschedule-${request.appointment_change_request_id}`,
        kind: 'reschedule' as const,
        requestId: request.appointment_change_request_id,
        name: request.requester_name || 'Unknown requester',
        startTime: request.requested_start_time ?? null,
        fromStartTime: request.current_start_time ?? null,
        blockers: [],
      }),
    );
    return [...bookings, ...reschedules];
  }, [data?.items, changeRequestData?.items, providerNames, serviceNames]);

  const finishAcceptance = useCallback(
    (card: RequestCardData, patientId: string | null) => {
      setResolved(previous => [
        ...previous,
        {
          key: `resolved-${card.key}`,
          name: card.name,
          startTime: card.startTime,
          patientId,
        },
      ]);
    },
    [],
  );

  const runAccept = useCallback(
    async (
      card: RequestCardData,
      body: { patient_id?: string | null; create_new?: boolean },
    ) => {
      if (!card.bookingProspectId) return;
      setBusyKey(card.key);
      setErrorMessage(null);
      try {
        const result = await acceptProspect({
          bookingProspectId: card.bookingProspectId,
          acceptProspectRequest: body,
        }).unwrap();
        if (result.decision_required) {
          setDecision({
            card,
            candidates: (result.candidates ?? []).map(candidate => ({
              patientId: candidate.patient_id,
              name: candidate.name ?? null,
              dob: candidate.dob ?? null,
              email: candidate.email ?? null,
              phone: candidate.phone ?? null,
              confidence: candidate.confidence,
            })),
          });
          return;
        }
        setDecision(null);
        finishAcceptance(card, result.patient_id ?? null);
      } catch {
        enqueueSnackbar('Could not confirm the booking', { variant: 'error' });
        errorTracking.track('Could not confirm the booking', 'booking_accept');
      } finally {
        setBusyKey(null);
      }
    },
    [acceptProspect, enqueueSnackbar, errorTracking, finishAcceptance],
  );

  const handleConfirm = useCallback(
    (card: RequestCardData) => {
      if (card.kind === 'booking') {
        bookingRequests.confirmClicked({
          appointment_id: card.appointmentId ?? '',
          source: 'queue',
        });
        void runAccept(card, {});
        return;
      }
      if (!card.requestId) return;
      setBusyKey(card.key);
      setErrorMessage(null);
      approveChangeRequest({ requestId: card.requestId })
        .unwrap()
        .then(() => enqueueSnackbar('New time confirmed', { variant: 'success' }))
        .catch((error: unknown) => setErrorMessage(decisionError(error)))
        .finally(() => setBusyKey(null));
    },
    [approveChangeRequest, bookingRequests, enqueueSnackbar, runAccept],
  );

  const handleDecline = useCallback(
    (card: RequestCardData) => {
      setBusyKey(card.key);
      setErrorMessage(null);
      if (card.kind === 'booking') {
        if (!card.bookingProspectId) return;
        bookingRequests.declineClicked({
          booking_prospect_id: card.bookingProspectId,
          source: 'queue',
        });
        declineBooking({ bookingProspectId: card.bookingProspectId })
          .unwrap()
          .then(() => enqueueSnackbar('Booking declined', { variant: 'success' }))
          .catch(() => {
            enqueueSnackbar('Could not decline the booking', {
              variant: 'error',
            });
            errorTracking.track(
              'Could not decline the booking',
              'booking_decline',
            );
          })
          .finally(() => setBusyKey(null));
        return;
      }
      if (!card.requestId) return;
      declineChangeRequest({ requestId: card.requestId })
        .unwrap()
        .then(() => enqueueSnackbar('Request declined', { variant: 'success' }))
        .catch((error: unknown) => setErrorMessage(decisionError(error)))
        .finally(() => setBusyKey(null));
    },
    [
      bookingRequests,
      declineBooking,
      declineChangeRequest,
      enqueueSnackbar,
      errorTracking,
    ],
  );

  const handleSelect = useCallback(
    (card: RequestCardData) => {
      setSelectedKey(previous => (previous === card.key ? null : card.key));
      if (card.startTime) {
        onNavigateToDate?.(card.startTime);
      }
    },
    [onNavigateToDate],
  );

  return (
    <>
      <BookingRequestsPanel
        requests={requests}
        resolved={resolved}
        isLoading={isLoading}
        errorMessage={errorMessage}
        subjectPlural={plural}
        busyKey={busyKey}
        selectedKey={selectedKey}
        onSelect={onNavigateToDate ? handleSelect : undefined}
        onConfirm={handleConfirm}
        onDecline={handleDecline}
        onClose={onClose}
      />
      {decision && (
        <IdentityDecisionDialog
          open
          requesterName={decision.card.name}
          candidates={decision.candidates}
          subjectSingular={singular}
          isSaving={busyKey === decision.card.key}
          onClose={() => setDecision(null)}
          onDecide={patientId => {
            void runAccept(
              decision.card,
              patientId ? { patient_id: patientId } : { create_new: true },
            );
          }}
        />
      )}
    </>
  );
}
